import type {
  AnyFieldSpecBase,
  EditUnit
} from 'v5/core/form-schema/formSchema.types';

/**
 * Unit ids contain `/` and `:`; RHF paths must not contain `.` or brackets.
 * Ids are unique and only use `[a-z0-9:/_-]`, so replacing every non-word
 * character with `_` stays collision-free for the id vocabulary the resolver
 * produces (`global/core:emails`, `ws:7/custom:42`, `global/group:7`).
 */
export function unitPath(unitId: EditUnit<AnyFieldSpecBase>['id']): string {
  return unitId.replace(/[^A-Za-z0-9_]/g, '_');
}

/** Field keys (`core:emails`, `custom:42`) contain `:`; keep them dot-free. */
export function fieldPath(basePath: string, fieldKey: string): string {
  const safeKey = fieldKey.replace(/[^A-Za-z0-9_]/g, '_');
  return basePath === '' ? safeKey : `${basePath}.${safeKey}`;
}

/**
 * Carried by each field's root and the roots of its parts (a rate's amount,
 * each fee), in display and in edit mode alike: what a double click opening
 * a unit was on, found again in its editor.
 */
export const FIELD_PATH_ATTRIBUTE = 'data-field-path';

/** Marks an element as the field, or the part of one, at `path`. */
export function fieldAnchor(path: string) {
  return { [FIELD_PATH_ATTRIBUTE]: path };
}

/**
 * Marks what a unit opened from its “+” goes to: a field where the next
 * entry is typed is focused, an add button (a list's) is pressed.
 */
export const UNIT_ADD_ATTRIBUTE = 'data-unit-add';

/** An entry of a list edited entry by entry, as a unit of its own. */
export function listEntryId(
  listId: EditUnit<AnyFieldSpecBase>['id'],
  rowKey: string
): string {
  return `${listId}/${rowKey}`;
}

/** Whether `targetId` names the unit `unitId` or one of its list's entries. */
export function isUnitOrEntry(
  targetId: string,
  unitId: EditUnit<AnyFieldSpecBase>['id']
): boolean {
  return targetId === unitId || targetId.startsWith(`${unitId}/`);
}

/** The root of the field at `path` under `root`, or of its first part. */
export function fieldElement(root: Element, path: string): HTMLElement | null {
  for (const element of root.querySelectorAll<HTMLElement>(
    `[${FIELD_PATH_ATTRIBUTE}]`
  )) {
    const elementPath = element.getAttribute(FIELD_PATH_ATTRIBUTE) ?? '';
    if (elementPath === path || elementPath.startsWith(`${path}.`)) {
      return element;
    }
  }
  return null;
}
